import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Settings,
  MapPin,
  Phone,
  ArrowLeft,
  Loader2,
  Search,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services.jsx';

const ParametrizacionCentrosPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [laundries, setLaundries] = useState([]);
  const [filteredLaundries, setFilteredLaundries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadLaundries = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, totalRecords } = await fetchLaundries({
        limit: 500,
        offset: 1,
        query: { estado: 'Activo' },
      });
      const list = data || [];
      setLaundries(list);
      setFilteredLaundries(list);
    } catch (error) {
      console.error('Error al cargar centros:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los centros de lavado',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLaundries();
  }, [loadLaundries]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLaundries(laundries);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredLaundries(
      laundries.filter(
        (l) =>
          (l.nombre || '').toLowerCase().includes(term) ||
          (l.direccion || '').toLowerCase().includes(term) ||
          (l.ciudad || '').toLowerCase().includes(term)
      )
    );
  }, [searchTerm, laundries]);

  const handleSelectCenter = (laundry) => {
    navigate(`/parametrizacion/centros/${laundry._id}`, {
      state: { laundry },
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="h-9 w-9"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Parametrización por Centro</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Selecciona un centro de lavado para parametrizar sus recursos
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Buscador */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar centro por nombre, dirección o ciudad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {filteredLaundries.length} de {laundries.length} centro(s)
              </p>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                <span className="ml-2 text-sm text-gray-500">Cargando centros...</span>
              </div>
            ) : filteredLaundries.length === 0 ? (
              <div className="text-center py-16">
                <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700">
                  {searchTerm ? 'No se encontraron centros' : 'No hay centros de lavado activos'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {searchTerm
                    ? 'Intenta con otros términos de búsqueda'
                    : 'Crea un centro de lavado primero desde la parametrización'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredLaundries.map((laundry, index) => (
                  <motion.div
                    key={laundry._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleSelectCenter(laundry)}
                    className="cursor-pointer group"
                  >
                    <Card className="h-full border hover:border-indigo-300 hover:shadow-md transition-all duration-200 group-hover:bg-indigo-50/30">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="p-2.5 bg-indigo-100 rounded-xl shrink-0 group-hover:bg-indigo-200 transition-colors">
                            <Building2 className="h-6 w-6 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {laundry.nombre}
                            </h3>
                            {laundry.direccion && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">{laundry.direccion}</span>
                              </p>
                            )}
                            {laundry.telefono && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Phone className="h-3 w-3 shrink-0" />
                                {laundry.telefono}
                              </p>
                            )}
                            {laundry.ciudad && (
                              <p className="text-xs text-gray-400 mt-1">{laundry.ciudad}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <Settings className="h-3.5 w-3.5 text-indigo-500" />
                            <span className="text-xs text-indigo-600 font-medium">
                              Parametrizar
                            </span>
                          </div>
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                            <ArrowLeft className="h-3 w-3 text-gray-400 rotate-180 group-hover:text-indigo-600 transition-colors" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ParametrizacionCentrosPage;
